import { getDownloadURL, getStorage, ref } from 'firebase/storage';
import { allergensMonthlyPdfStoragePath } from '../../functions/src/admin/paths';
import { getStartOfWeekMonday } from '../../functions/src/api/dates';
import type { AllergensList } from '../types';
import { currentMenu } from './firebase';
import { Language } from './Lang';

/** Student/person has at least one selected allergen or non-empty free-text intolerances. */
export function personHasDefinedAllergies(allergies?: AllergensList): boolean {
    if (!allergies) {
        return false;
    }
    const foodIntolerances = allergies.foodIntolerances;
    if (foodIntolerances && String(foodIntolerances).trim()) {
        return true;
    }
    return Object.keys(allergies).some((k) => k !== 'foodIntolerances' && !!(allergies as Record<string, unknown>)[k]);
}

/** Omit allergies on save when user chose “yes” but left every field empty. */
export function normalizeAllergiesForStorage(allergies?: AllergensList | null): AllergensList | null | undefined {
    if (allergies == null) {
        return allergies;
    }
    return personHasDefinedAllergies(allergies) ? allergies : null;
}

/**
 * Utility functions for handling allergen PDF files in Firebase Storage
 */

/**
 * Get the date string (YYYYMM) for allergen PDF based on context
 * @param targetDate - The target date (for popups: order date, for monthly: selected month)
 * @param isMonthlyMenu - Whether this is for monthly menu (use month) or popup (use start of week)
 * @returns Date string in YYYYMM format
 */
function getDateStringForAllergen(targetDate: Date, isMonthlyMenu: boolean = false): string {

    if (!targetDate)
        targetDate = new Date();

    const date = isMonthlyMenu ? targetDate : getStartOfWeekMonday(targetDate);

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${year}${month}`;
}

/** YYYYMM for Storage / Cloud Functions (monthly vs week context). */
export function getAllergenYearMonth(targetDate: Date, isMonthlyMenu: boolean = false): string {
    return getDateStringForAllergen(targetDate, isMonthlyMenu);
}

/**
 * Get the Firebase Storage download URL for an allergen PDF file (throws if not found).
 * Used by order app: MealsPopup, MonthlyMenu, etc.
 * @param lang - Language code ('el' or 'en')
 * @param targetDate - The target date (for popups: order date, for monthly: selected month)
 * @param isMonthlyMenu - Whether this is for monthly menu (use month) or popup (use start of week)
 * @returns Promise that resolves to Firebase Storage download URL for the allergen PDF
 */
export async function getAllergenPdfUrl(lang: Language, targetDate?: Date, isMonthlyMenu: boolean = false): Promise<string> {
    const filePath = getAllergenPdfStoragePath(lang, targetDate, isMonthlyMenu);
    if (!filePath) throw new Error('Allergen PDF: targetDate is required');

    try {
        const fileRef = ref(getStorage(), filePath);
        return await getDownloadURL(fileRef);
    } catch (error) {
        throw new Error(`Allergen PDF file not found: ${filePath.split('/').pop()}`);
    }
}

/**
 * Get the download URL for an allergen PDF if it exists (same path as getAllergenPdfUrl).
 * Returns null when the file is not in Storage. Use in admin to show/replace existing files.
 */
export async function getAllergenPdfUrlOptional(lang: Language, targetDate?: Date, isMonthlyMenu: boolean = false): Promise<string | null> {
    if (!targetDate) return null;
    try {
        return await getAllergenPdfUrl(lang, targetDate, isMonthlyMenu);
    } catch {
        return null;
    }
}

/**
 * Get the Firebase Storage path for an allergen PDF (single source of truth for path convention).
 * Used for upload in admin and for URL fetch in getAllergenPdfUrl.
 * @param lang - Language code ('el' or 'en')
 * @param targetDate - The target date (for popups: order date, for monthly: selected month)
 * @param isMonthlyMenu - Whether this is for monthly menu (use month) or popup (use start of week)
 * @returns Full storage path e.g. "allergens/202602/202602-Gefsinus1-allergens-el.pdf"
 */
export function getAllergenPdfStoragePath(lang: Language, targetDate?: Date, isMonthlyMenu: boolean = false): string {
    if (!targetDate) return '';
    const yearMonth = getDateStringForAllergen(targetDate, isMonthlyMenu);
    return allergensMonthlyPdfStoragePath(yearMonth, currentMenu, lang);
}

/**
 * Get the download filename for an allergen PDF
 * @param lang - Language code ('el' or 'en')
 * @param targetDate - The target date (for popups: order date, for monthly: selected month)
 * @param isMonthlyMenu - Whether this is for monthly menu (use month) or popup (use start of week)
 * @returns Suggested filename for download
 */
export function getAllergenPdfDownloadName(lang: Language, targetDate?: Date, isMonthlyMenu: boolean = false): string {

    const dateString = getDateStringForAllergen(targetDate, isMonthlyMenu);

    return `${currentMenu} allergens list - ${lang} (${dateString}).pdf`;
}
